import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Volume2, Send, Loader2, Sparkles } from 'lucide-react';
import api from '../../lib/api';
import { useTranslation } from '../../i18n';

export function AIConcierge({ onComplete, initialContext }) {
  const { language, t } = useTranslation();
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: t('concierge.welcome') || "Welcome to PocketFiler! I'm your AI Concierge. Tell me about your business and what you're looking to accomplish, and I'll help you get set up."
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [speaking, setSpeaking] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await api.post('/ai/concierge', {
        message: userMessage,
        context: initialContext,
        language: language
      });

      setMessages(prev => [...prev, { role: 'assistant', content: response.message }]);
      
      if (response.suggestions) {
        setSuggestions(response.suggestions);
      }

      if (response.action === 'complete' || response.action === 'setup_profile') {
        setTimeout(() => {
          onComplete?.(response.suggestions);
        }, 2000);
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I apologize, but I encountered an issue. Please try again."
      }]);
    } finally {
      setLoading(false);
    }
  };

  const speakMessage = async (text) => {
    setSpeaking(true);
    try {
      const response = await api.post('/ai/tts', { text, lang: 'en' });
      const audio = new Audio(api.getAudioUrl(response.audio_url));
      audio.onended = () => setSpeaking(false);
      audio.play();
    } catch (error) {
      console.error('TTS error:', error);
      setSpeaking(false);
    }
  };

  return (
    <div className="flex flex-col h-full max-h-[600px]">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`message-bubble ${msg.role === 'user' ? 'message-bubble-user' : 'message-bubble-ai'}`}
              data-testid={msg.role === 'assistant' ? 'ai-welcome-message' : undefined}
            >
              <p className="text-sm">{msg.content}</p>
              {msg.role === 'assistant' && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 h-7 px-2 text-xs"
                  onClick={() => speakMessage(msg.content)}
                  disabled={speaking}
                  data-testid="voice-toggle-button"
                  aria-label="Play voice"
                >
                  <Volume2 className="h-3 w-3 mr-1" />
                  {speaking ? 'Speaking...' : 'Listen'}
                </Button>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="message-bubble message-bubble-ai">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Suggestions preview */}
      {suggestions && (
        <Card className="mx-4 mb-2 border-accent/30 bg-accent/5">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-accent" />
              <span className="text-sm font-medium">Suggested Setup</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {suggestions.role && (
                <div>
                  <span className="text-muted-foreground">Role:</span>{' '}
                  <span className="font-medium capitalize">{suggestions.role}</span>
                </div>
              )}
              {suggestions.industry && (
                <div>
                  <span className="text-muted-foreground">Industry:</span>{' '}
                  <span className="font-medium capitalize">{suggestions.industry}</span>
                </div>
              )}
              {suggestions.languages && suggestions.languages.length > 0 && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">Languages:</span>{' '}
                  {suggestions.languages.map(lang => (
                    <span key={lang} className="language-chip mr-1">{lang.toUpperCase()}</span>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-4 border-t bg-background/80">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Tell me about your business..."
            disabled={loading}
            data-testid="onboarding-input"
            className="flex-1"
          />
          <Button
            type="submit"
            disabled={loading || !input.trim()}
            data-testid="step-next-button"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </form>
    </div>
  );
}
